import { useState } from 'react';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { API_BASE_URL } from '@/lib/api-client'; // Import API_BASE_URL
import { Skeleton } from '@/components/ui/skeleton'; // Import Skeleton
import { useI18nContext } from '@/i18n/i18n-react';
import { FixedHeader } from '@/components/fixed-header';

interface AccountDeletionForm {
  email: string;
}

interface PublicTenantInfo {
  name: string;
  iconUrl: string | null;
}

// Add API function for public tenant info
async function getPublicTenantInfo(
  tenantSlug: string
): Promise<PublicTenantInfo> {
  const response = await fetch(`${API_BASE_URL}/public/${tenantSlug}`);

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to get tenant information');
  }

  return response.json();
}

export default function AccountDeletionPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { LL } = useI18nContext();

  const [isRequestSent, setIsRequestSent] = useState(false);

  const tenant = useQuery({
    queryKey: ['tenant-public', tenantSlug],
    queryFn: () => getPublicTenantInfo(tenantSlug ?? ''),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AccountDeletionForm>();

  const mutation = useMutation({
    mutationFn: async (email: string) => {
      // Update API call to use public endpoint
      const response = await fetch(
        `${API_BASE_URL}/public/${tenantSlug}/request-account-deletion`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: LL.general.unknownError() }));
        throw new Error(
          error.message || LL.accountDeletion.failedRequestDeletion()
        );
      }
    },
    onSuccess: () => {
      setIsRequestSent(true);
      reset(); // Clear the form
    },
    onError: (error: Error) => {
      toast.error(LL.general.error(), {
        description: error.message,
      });
    },
  });

  const onSubmit = (data: AccountDeletionForm) => {
    mutation.mutate(data.email);
  };

  return (
    <div className="flex justify-center items-center h-screen">
      <FixedHeader />
      <Card className="w-[350px]">
        <CardHeader className="flex flex-col items-center">
          {tenant?.data?.iconUrl && (
            <img
              src={tenant.data?.iconUrl}
              alt={`${tenant?.data?.name} icon`}
              className="w-12 h-12 mb-2"
            />
          )}
          {tenant.isLoading ? ( // Show skeleton while loading tenant info
            <Skeleton className="h-6 w-32" />
          ) : (
            <CardTitle>{tenant?.data?.name}</CardTitle>
          )}
        </CardHeader>
        <CardContent>
          {isRequestSent ? (
            <div className="text-center font-normal">
              {LL.accountDeletion.requestSent()}
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-center mb-4">
                {LL.accountDeletion.title()}
              </h2>
              <p className="text-sm text-muted-foreground text-center mb-4">
                {LL.accountDeletion.emailPrompt()}
              </p>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Input
                    id="email"
                    type="email"
                    placeholder={LL.accountDeletion.emailPlaceholder()}
                    {...register('email', {
                      required: LL.accountDeletion.emailRequired(),
                    })}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={mutation.status === 'pending'}
                >
                  {mutation.status === 'pending'
                    ? LL.accountDeletion.submitting()
                    : LL.accountDeletion.submitRequest()}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
